import { useState, useEffect } from "react";
import { supabaseCustomRole } from "@/lib/supabase/customRoleClient";
import { Dealer } from "@/types/dealer";
import { toast } from "sonner";

// Funzione per calcolare la distanza tra due punti geografici usando la formula haversine
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): string {
  const R = 6371; // Raggio della Terra in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  // Formato: metri se < 1 km, altrimenti km con 1 decimale
  return distance < 1 
    ? `${Math.round(distance * 1000)} m` 
    : `${distance.toFixed(1)} km`;
}

export function useDealerDetail(id: string | undefined) {
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [userDistance, setUserDistance] = useState<string | null>(null);

  // Funzione per ottenere la posizione dell'utente e calcolare la distanza
  const calculateUserDistance = (dealerLat: number, dealerLon: number) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLon = position.coords.longitude;
          const distance = calculateDistance(userLat, userLon, dealerLat, dealerLon);
          setUserDistance(distance);
        },
        (error) => {
          console.log("Geolocation error:", error);
          setUserDistance(null);
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    }
  };

  useEffect(() => {
    const fetchDealer = async () => {
      if (!id) return;

      try {
        // Start with loading state, reset error state
        setLoading(true);
        setError(false);
        
        // Use the vw_dealers_with_ratings view to get dealer with rating data
        const { data: dealerData, error: dealerError } = await supabaseCustomRole
          .from("vw_dealers_with_ratings")
          .select("*")
          .eq("id", id)
          .single();

        if (dealerError) throw dealerError;
        
        if (!dealerData) {
          throw new Error(`No dealer found with ID: ${id}`);
        }
        
        console.log("Dealer data from view:", dealerData);

        // Get the rating from the view (already calculated as average_rating)
        const rating = dealerData.average_rating || 0;

        console.log("Fetching quotations for dealer:", id);
        const { data: quotationData, error: quotationError } = await supabaseCustomRole
          .from('dealer_quotations')
          .select('*')
          .eq('dealer_id', id)
          .eq('metal', 'ORO')
          .order('created_at', { ascending: false })
          .limit(1);

        if (quotationError) {
          console.error("Error fetching quotations:", quotationError);
        } else {
          console.log("Quotation data:", quotationData);
        }

        let buySpread;
        if (quotationData && quotationData.length > 0) {
          const quotation = quotationData[0];
          buySpread = quotation.spread_type === 'FIXED' 
            ? quotation.spread_fixed 
            : quotation.spread_percentage;
          console.log("Calculated buySpread:", buySpread, "from quotation:", quotation);
        } else {
          console.log("No quotation data found for dealer");
        }

        console.log("Dealer properties:");
        Object.keys(dealerData).forEach(key => {
          console.log(`${key}: ${dealerData[key]}`);
        });
        console.log("Final rating from view:", rating);

        // Create the formatted dealer object
        const formattedDealer: Dealer = {
          id: dealerData.id,
          name: dealerData.name,
          address: dealerData.address,
          rating: rating,
          is_verified: dealerData.is_verified || false,
          activated: dealerData.activated || false,
          isFavorite: false,
          latitude: dealerData.latitude,
          longitude: dealerData.longitude,
          buySpread: buySpread,
          image_url: dealerData.image_url,
          postal_code: dealerData.postal_code,
          phone: dealerData.phone,
          website: dealerData.website,
          opening_hours: dealerData.opening_hours,
          city: dealerData.city,
          facebook: dealerData.social_facebook,
          instagram: dealerData.social_instagram,
          twitter: dealerData.social_twitter,
          youtube: dealerData.social_youtube,
          linkedin: dealerData.social_linkedin,
        };

        console.log("Formatted dealer data:", formattedDealer);
        setDealer(formattedDealer);
        setError(false);

        // Calcola la distanza se il dealer ha coordinate
        if (dealerData.latitude && dealerData.longitude) {
          calculateUserDistance(dealerData.latitude, dealerData.longitude);
        }
      } catch (error) {
        console.error("Error fetching dealer:", error);
        toast.error("Impossibile caricare i dati del commerciante");
        setError(true);
        setDealer(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDealer();
  }, [id]);

  return { dealer, loading, error, userDistance };
}
